import { Link } from "react-router-dom";
import Badge from "@/components/base/Badge";
import EmptyState from "@/components/base/EmptyState";
import StatCard from "@/pages/proprietaire/components/StatCard";
import { cn } from "@/utils/cn";
import { formatDate } from "@/utils/format";
import { useSuperAdmin } from "./data/SuperAdminProvider";
import { usePlatformData } from "@/store/platform/PlatformDataProvider";
import { useBilling } from "@/store/billing/BillingProvider";
import { useAdministrators } from "@/store/administrators/AdministratorsProvider";
import {
  AUDIT_ACTION_ICONS,
  AUDIT_STATUT_LABELS,
  AUDIT_STATUT_TONES,
  COMPTE_STATUT_LABELS,
  COMPTE_STATUT_TONES,
  COMPTE_TYPE_CONFIG,
  type CompteStatut,
  type CompteType,
} from "./data/types";

const MODULE_LINKS: { type: CompteType; hint: string }[] = [
  { type: "proprietaire", hint: "Statut, coordonnées, patrimoine" },
  { type: "agence", hint: "Formule, rattachements, employés" },
  { type: "utilisateur", hint: "Statut, rôle, journal d'audit" },
];

const STATUT_ORDER: CompteStatut[] = ["actif", "en_attente", "suspendu", "desactive"];

export default function SuperAdminDashboard() {
  const { comptes, audit, administrateur, erreur, recharger } = useSuperAdmin();
  const { immeubles, biens, locataires, contrats } = usePlatformData();
  const { abonnements, paiements } = useBilling();
  const { administrateurs } = useAdministrators();
  const administrateursActifs = administrateurs.filter((item) => item.statut === "actif").length;

  const byType = (type: CompteType) => comptes.filter((compte) => compte.type === type).length;

  const statutCounts = STATUT_ORDER.map((statut) => ({
    statut,
    count: comptes.filter((compte) => compte.statut === statut).length,
  }));

  const recentAudit = audit.slice(0, 6);
  const total = comptes.length || 1;
  const inscriptionsEnAttente = comptes.filter((compte) => compte.statut === "en_attente").length;

  return (
    <div className="flex flex-col gap-6">
      <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
        <p className="text-xs font-label font-medium uppercase tracking-wide text-foreground-400">
          Super administration
        </p>
        <h2 className="mt-1 font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
          Bienvenue, {administrateur}
        </h2>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
          Pilotez les comptes de la plateforme : activez, suspendez ou désactivez les propriétaires, agences et
          utilisateurs. Chaque action administrative importante est automatiquement tracée dans le journal d'audit.
        </p>
        <Link
          to="/super-admin/statistiques"
          className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
        >
          <i className="ri-bar-chart-box-line text-base" aria-hidden="true" />
          Voir les statistiques
        </Link>
      </section>

      {erreur && (
        <div className="flex flex-col items-start gap-3 rounded-lg border border-primary-200 bg-primary-50/60 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="flex items-center gap-2 text-sm font-label font-medium text-primary-800">
            <i className="ri-error-warning-line text-base" aria-hidden="true" />
            {erreur}
          </p>
          <button
            type="button"
            onClick={() => void recharger()}
            className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-4 py-2 text-sm font-label font-medium text-primary-700 transition-colors hover:bg-primary-100 cursor-pointer"
          >
            <i className="ri-refresh-line text-base" aria-hidden="true" />
            Réessayer
          </button>
        </div>
      )}

      {inscriptionsEnAttente > 0 && (
        <Link
          to="/super-admin/inscriptions"
          className="flex items-center justify-between gap-4 rounded-lg border border-accent-300 bg-accent-50 px-5 py-4 transition-colors hover:bg-accent-100 cursor-pointer"
        >
          <span className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-500 text-background-50">
              <i className="ri-user-received-2-line text-xl" aria-hidden="true" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-heading font-semibold text-foreground-950">
                {inscriptionsEnAttente} inscription{inscriptionsEnAttente > 1 ? "s" : ""} en attente de validation
              </span>
              <span className="mt-0.5 block text-xs text-foreground-600">
                Des demandes de compte attendent votre décision (valider ou refuser).
              </span>
            </span>
          </span>
          <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-xs font-label font-medium text-accent-800">
            Traiter les demandes
            <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
          </span>
        </Link>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon="ri-group-line" label="Comptes au total" value={String(comptes.length)} tone="primary" />
        <StatCard icon="ri-user-star-line" label="Propriétaires" value={String(byType("proprietaire"))} tone="secondary" />
        <StatCard icon="ri-building-2-line" label="Agences" value={String(byType("agence"))} tone="accent" />
        <StatCard icon="ri-user-settings-line" label="Utilisateurs" value={String(byType("utilisateur"))} tone="neutral" />
      </div>

      <section className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-heading text-base font-semibold text-foreground-950">Patrimoine &amp; abonnements</h3>
          <Link
            to="/super-admin/rapports"
            className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-label font-medium text-primary-700 transition-colors hover:text-primary-800 cursor-pointer"
          >
            Rapports
            <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
          </Link>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Link to="/super-admin/immeubles" className="cursor-pointer">
            <StatCard icon="ri-building-2-line" label="Immeubles" value={String(immeubles.length)} tone="primary" />
          </Link>
          <Link to="/super-admin/biens" className="cursor-pointer">
            <StatCard icon="ri-home-4-line" label="Biens" value={String(biens.length)} tone="secondary" />
          </Link>
          <Link to="/super-admin/locataires" className="cursor-pointer">
            <StatCard icon="ri-user-3-line" label="Locataires" value={String(locataires.length)} tone="accent" />
          </Link>
          <Link to="/super-admin/contrats" className="cursor-pointer">
            <StatCard icon="ri-file-text-line" label="Contrats" value={String(contrats.length)} tone="neutral" />
          </Link>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Link to="/super-admin/abonnements" className="cursor-pointer">
            <StatCard icon="ri-repeat-2-line" label="Abonnements" value={String(abonnements.length)} tone="primary" />
          </Link>
          <Link to="/super-admin/paiements" className="cursor-pointer">
            <StatCard icon="ri-hand-coin-line" label="Paiements" value={String(paiements.length)} tone="secondary" />
          </Link>
          <Link to="/super-admin/administrateurs" className="cursor-pointer">
            <StatCard icon="ri-admin-line" label="Administrateurs actifs" value={String(administrateursActifs)} tone="accent" />
          </Link>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <section className="flex flex-col rounded-lg border border-background-200 bg-background-50 lg:col-span-2">
          <header className="flex items-center justify-between gap-3 border-b border-background-200 px-4 py-3.5 md:px-5">
            <span className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                <i className="ri-history-line text-base" aria-hidden="true" />
              </span>
              <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">
                Dernières actions administratives
              </h3>
            </span>
            <Link
              to="/super-admin/audit"
              className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-label font-medium text-primary-700 transition-colors hover:text-primary-800 cursor-pointer"
            >
              Journal d'audit
              <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
            </Link>
          </header>
          <div className="p-4 md:p-5">
            {recentAudit.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-md bg-background-100/70 px-6 py-10 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-primary-600">
                  <i className="ri-shield-check-line text-xl" aria-hidden="true" />
                </span>
                <p className="mt-3 text-sm font-heading font-semibold text-foreground-950">Aucune action enregistrée</p>
                <p className="mt-1 max-w-sm text-xs text-foreground-600">
                  Les actions administratives (création, activation, suspension, désactivation...) apparaîtront ici.
                </p>
              </div>
            ) : (
              <ul className="flex flex-col divide-y divide-background-200">
                {recentAudit.map((entry) => (
                  <li key={entry.id} className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-600">
                        <i className={cn(AUDIT_ACTION_ICONS[entry.actionKey], "text-base")} aria-hidden="true" />
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-label font-medium text-foreground-950">
                          {entry.action} · {entry.compteNom}
                        </p>
                        <p className="mt-0.5 truncate text-xs text-foreground-500">
                          {COMPTE_TYPE_CONFIG[entry.compteType].singular} · {formatDate(entry.date, true)}
                        </p>
                      </div>
                    </div>
                    <Badge tone={AUDIT_STATUT_TONES[entry.statut]}>{AUDIT_STATUT_LABELS[entry.statut]}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
          <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-50 text-accent-700">
              <i className="ri-pie-chart-2-line text-base" aria-hidden="true" />
            </span>
            <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">Répartition par statut</h3>
          </header>
          <div className="flex flex-col gap-4 p-4 md:p-5">
            {statutCounts.map(({ statut, count }) => (
              <div key={statut} className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-label font-medium text-foreground-800">
                    {COMPTE_STATUT_LABELS[statut]}
                  </span>
                  <span className="text-sm font-label font-semibold text-foreground-950">{count}</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-background-100">
                  <div
                    className={cn(
                      "h-full rounded-full",
                      statut === "actif"
                        ? "bg-secondary-500"
                        : statut === "en_attente"
                          ? "bg-accent-500"
                          : statut === "suspendu"
                            ? "bg-primary-500"
                            : "bg-foreground-300"
                    )}
                    style={{ width: `${Math.min(100, (count / total) * 100)}%` }}
                  />
                </div>
              </div>
            ))}
            {comptes.length === 0 && (
              <p className="text-xs text-foreground-500">
                Aucun compte pour le moment. Les statistiques apparaîtront une fois des comptes créés.
              </p>
            )}
          </div>
        </section>
      </div>

      <section className="flex flex-col gap-4">
        <h3 className="font-heading text-base font-semibold text-foreground-950">Administration des comptes</h3>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {MODULE_LINKS.map(({ type, hint }) => {
            const config = COMPTE_TYPE_CONFIG[type];
            const count = byType(type);
            return (
              <Link
                key={type}
                to={config.basePath}
                className="card-lift flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-foreground-300 md:p-5 cursor-pointer"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                  <i className={cn(config.icon, "text-xl")} aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-heading text-base font-semibold text-foreground-950">{config.label}</p>
                  <p className="mt-0.5 text-xs text-foreground-600">{hint}</p>
                  <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-background-100 px-2.5 py-0.5 text-xs font-label font-medium text-foreground-700">
                    {count} compte{count > 1 ? "s" : ""}
                  </span>
                </div>
                <i className="ri-arrow-right-s-line shrink-0 text-base text-foreground-400" aria-hidden="true" />
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}